import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '../../services/user.service';
import { DashboardService } from '../../../dashboard/services/dashboard.service';
import { SignalRService } from 'src/app/core/services/signalr.service';
import { Worker } from '../../models/worker.model';

@Component({
  selector: 'app-workers-list',
  templateUrl: './workers-list.component.html',
  styleUrls: ['./workers-list.component.css']
})
export class WorkersListComponent implements OnInit, OnDestroy {

  workers: Worker[]         = [];
  filteredWorkers: Worker[] = [];
  pagedWorkers: Worker[]    = [];

  loading     = true;
  searchQuery = '';

  currentPage   = 1;
  pageSize      = 10;
  totalPages    = 1;
  pageNumbers: number[] = [];

  Math = Math;

  private avatarColors = [
    'linear-gradient(135deg, #1F3A5F, #2BB0A6)',
    'linear-gradient(135deg, #2BB0A6, #1F8F88)',
    'linear-gradient(135deg, #3498DB, #1F3A5F)',
    'linear-gradient(135deg, #F5A623, #E8821A)',
    'linear-gradient(135deg, #2ECC71, #1A9E4C)',
    'linear-gradient(135deg, #9B59B6, #6C3483)',
    'linear-gradient(135deg, #E74C3C, #C0392B)',
    'linear-gradient(135deg, #1ABC9C, #148F77)',
  ];

  constructor(
    private userService: UserService,
    private dashboardService: DashboardService,  // ✅ new
    private signalR: SignalRService               // ✅ new
  ) {}

  ngOnInit(): void {
    this.loadWorkers();

    // ✅ Listen for real-time presence changes via SignalR
    this.signalR.startConnection().then(() => {

      this.signalR.on('WorkerOnline', (data: any) => {
        const worker = this.workers.find(w => w.id === data.userId);
        if (worker) {
          worker.isOnline = true;
          this.applySearch(); // ✅ refresh filtered/paged lists
        }
      });

      this.signalR.on('WorkerOffline', (data: any) => {
        const worker = this.workers.find(w => w.id === data.userId);
        if (worker) {
          worker.isOnline = false;
          this.applySearch();
        }
      });

    }).catch(err => {
      console.warn('SignalR not available on workers list:', err);
    });
  }

  ngOnDestroy(): void {
    // SignalR connection is shared — don't stop it here
  }

  loadWorkers(): void {
    this.loading = true;

    // ✅ Load workers and presence data in parallel
    this.userService.getWorkers().subscribe({
      next: (workers) => {
        this.workers = workers;

        // ✅ Fetch live worker presence to set isOnline
        this.dashboardService.getLiveWorkers().subscribe({
          next: (liveWorkers) => {
            // Merge isOnline status into workers list
            this.workers.forEach(worker => {
              const presence = liveWorkers.find(lw => lw.userId === worker.id);
              worker.isOnline = presence?.isOnline ?? false;
            });

            this.applySearch();
            this.loading = false;
          },
          error: () => {
            // If presence fetch fails, just show all as offline
            this.applySearch();
            this.loading = false;
          }
        });
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.applySearch();
  }

  private applySearch(): void {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredWorkers = q
      ? this.workers.filter(w =>
          w.fullName?.toLowerCase().includes(q) ||
          w.email?.toLowerCase().includes(q)
        )
      : [...this.workers];

    this.buildPagination();
    this.updatePage();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updatePage();
  }

  private buildPagination(): void {
    this.totalPages  = Math.max(1, Math.ceil(this.filteredWorkers.length / this.pageSize));
    this.pageNumbers = Array.from({ length: this.totalPages }, (_, i) => i + 1)
      .filter(p => Math.abs(p - this.currentPage) <= 2);
  }

  private updatePage(): void {
    const start       = (this.currentPage - 1) * this.pageSize;
    this.pagedWorkers = this.filteredWorkers.slice(start, start + this.pageSize);
    this.buildPagination();
  }

  get onlineCount(): number {
    return this.workers.filter(w => w.isOnline).length;
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name) return this.avatarColors[0];
    const idx = name.charCodeAt(0) % this.avatarColors.length;
    return this.avatarColors[idx];
  }
}